import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchUsageInfo } from "../api";
import { UsageResponse } from "../types";
import { toast } from "react-toastify";
import { useAuth } from "../../../features/auth/hooks";
import { isSelectableMonth } from "../utils/months";

const MONTH_RE = /^\d{4}-\d{2}$/;

export function useUsageData() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [usageData, setUsageData] = useState<UsageResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Read the month query param. Invalid values, or values earlier than the
  // credits system's start month, fall through to undefined (server defaults
  // to current UTC month).
  const monthParam = searchParams.get("month");
  const month =
    monthParam && MONTH_RE.test(monthParam) && isSelectableMonth(monthParam)
      ? monthParam
      : undefined;

  useEffect(() => {
    if (!isAuthenticated || authLoading) {
      return;
    }

    const loadUsageData = async () => {
      setIsLoading(true);
      try {
        const data = await fetchUsageInfo(month);
        setUsageData(data);
        setErrorMessage(null);
      } catch (error: any) {
        console.error("Failed to fetch usage data:", error);
        const errorMsg = error.message || "Failed to load usage data";
        toast.error(errorMsg);
        setErrorMessage(errorMsg);
      } finally {
        setIsLoading(false);
      }
    };

    loadUsageData();
  }, [isAuthenticated, authLoading, month]);

  return { usageData, errorMessage, isLoading: isLoading || authLoading };
}
